import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Home, Wrench, ArrowRight, HomeIcon, UtensilsCrossed, Bath } from 'lucide-react';
import SearchableDropdown from '../components/SearchableDropdown';
import { US_STATES, getCitiesForState } from '../utils/statesAndCities';
import { trackEvent, trackProjectEstimate } from '../config/firebase';

const ConceptualEstimate = () => {
  const [constructionType, setConstructionType] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const navigate = useNavigate();

  // Update available cities when state changes
  useEffect(() => {
    if (state) {
      const cities = getCitiesForState(state);
      setAvailableCities(cities);
      // Clear city if it's not in the new state's cities
      if (city && !cities.includes(city)) {
        setCity('');
      }
    } else {
      setAvailableCities([]);
      setCity('');
    }
  }, [state, city]);

  const constructionTypes = [
    {
      id: 'single-family',
      label: 'Single Family Home',
      icon: Home,
      description: 'Detached single-family residential property'
    },
    {
      id: 'multi-family',
      label: 'Multi-Family Home',
      icon: Building,
      description: 'Duplex, triplex, or apartment building'
    },
    {
      id: 'home-improvements',
      label: 'Home Improvements',
      icon: Wrench,
      description: 'Renovations, additions, or modifications'
    },
    {
      id: 'adu',
      label: 'Accessory Dwelling Unit (ADU)',
      icon: HomeIcon,
      description: 'Detached or attached secondary dwelling unit'
    },
    {
      id: 'kitchen-upgrade',
      label: 'Kitchen Upgrade',
      icon: UtensilsCrossed,
      description: 'Kitchen remodeling and renovation'
    },
    {
      id: 'bathroom-upgrade',
      label: 'Bathroom Upgrade',
      icon: Bath,
      description: 'Bathroom remodeling and renovation'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (constructionType && city && state) {
      // Track form submission
      trackEvent('form_submit', {
        form_type: 'conceptual_estimate',
        project_type: constructionType,
        city: city,
        state: state
      });
      
      // Track project estimate request
      trackProjectEstimate(constructionType, city, state, null);
      
      // Store the selections in localStorage for use in the question flow
      localStorage.setItem('projectData', JSON.stringify({
        constructionType,
        city,
        state,
        timestamp: new Date().toISOString()
      }));
      navigate('/question-flow');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
          Conceptual Cost Estimate
        </h1>
        <p className="text-base sm:text-lg text-gray-600">
          Tell us about your construction project to get started
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* Construction Type Selection */}
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
            What are you trying to build?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {constructionTypes.map((type) => (
              <div
                key={type.id}
                className={`option-card ${constructionType === type.id ? 'selected' : ''}`}
                onClick={() => {
                  setConstructionType(type.id);
                  trackEvent('project_type_selected', {
                    project_type: type.id,
                    project_label: type.label
                  });
                }}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <type.icon 
                    size={24} 
                    className={constructionType === type.id ? 'text-primary-600' : 'text-gray-600'}
                  />
                  <h3 className="font-medium text-gray-900">{type.label}</h3>
                </div>
                <p className="text-sm text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Location Selection */}
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
            Where is your project located?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <SearchableDropdown
                options={US_STATES}
                value={state}
                onChange={setState}
                placeholder="Search or select state..."
                label="State"
                id="state"
                required={true}
              />
            </div>
            <div>
              <SearchableDropdown
                options={availableCities}
                value={city}
                onChange={setCity}
                placeholder={state ? "Search, select, or enter city name..." : "Please select a state first"}
                label="City"
                id="city"
                required={true}
                disabled={!state}
                allowCustomInput={true}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-4">
          <button
            type="submit"
            disabled={!constructionType || !city || !state}
            className="btn-primary flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <span>Continue to Questions</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConceptualEstimate;
